// delete this entire file when cleaning up isVisualRefreshEnabled FG
import React, { Component, useCallback, useRef, type PointerEvent } from 'react';
import noop from 'lodash/noop';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Flex, xcss } from '@atlaskit/primitives';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { TransitionComponent } from '@atlassian/jira-common-directory-v2/src/components/transition-component/index.tsx';
import { injectIntl } from '@atlassian/jira-intl';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import messages from './messages.tsx';

type Props = {
	isPrivate: boolean;
	isSimple: boolean;
	canEdit: boolean;
	defaultOpen: boolean;
} & Intl & {
		name?: string;
		showSpinner: boolean;
		editBoardUrl: string;
		onEdit: (arg1: AnalyticsEvent) => void;
		onDelete: (
			event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
			arg1: AnalyticsEvent,
		) => void;
		onCopy: (
			event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
			arg1: AnalyticsEvent,
		) => void;
		onMove: (arg1: AnalyticsEvent) => void;
		moreActionsRef?: React.Ref<HTMLButtonElement | null>;
		returnFocus?: () => void;
	};

type State = {
	isOpen: boolean;
};

const DropdownMenuWithAnalytics = ComponentWithAnalytics('dropdownMenu', {
	onOpenChange: 'open',
})(DropdownMenu);

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

// eslint-disable-next-line jira/react/no-class-components
class ActionsOld extends Component<Props, State> {
	static defaultProps = {
		name: undefined,
		isPrivate: false,
		isSimple: false,
		canEdit: true,
		defaultOpen: false,
		showSpinner: false,
		editBoardUrl: '',
		onEdit: noop,
		onDelete: noop,
		onCopy: noop,
		onMove: noop,
	};

	state = {
		isOpen: this.props.defaultOpen,
	};

	// @ts-expect-error - TS7031 - Binding element 'isOpen' implicitly has an 'any' type.
	onOpenChange = ({ isOpen }) => {
		this.setState(() => ({ isOpen }));
	};

	onEdit = (e: PointerEvent, analytics: AnalyticsEvent) => {
		this.props.onEdit(analytics);
		this.delayedClose();
	};

	/**
	 * Close the menu with a minimal delay so the inner handlers are able to react on keypress properly.
	 * In its current implementation if we close the menu right away it won't open the focused `href`.
	 * Ideally we have to get right of this and few more methods when the bug linked below is fixed.
	 * @see https://ecosystem.atlassian.net/browse/AK-4467
	 */
	delayedClose() {
		setTimeout(() => this.setState(() => ({ isOpen: false })));
	}

	renderSettingsAction() {
		const {
			intl: { formatMessage },
			editBoardUrl,
		} = this.props;

		return (
			<TransitionComponent href={editBoardUrl} onClick={this.onEdit}>
				{(props) => (
					/* @ts-expect-error - Type '{ children: string; href: string; onClick: OnClick; }' is not assignable to type 'Omit<{ children: ReactNode; component?: ComponentType<PropsWithChildren<CustomItemComponentProps>> | undefined; ... 14 more ...; key?: Key | ... 1 more ... | undefined; }, keyof WithAnalyticsEventsProps>'. */
					<DropdownItemWithAnalytics {...props}>
						{formatMessage(messages.editAction)}
					</DropdownItemWithAnalytics>
				)}
			</TransitionComponent>
		);
	}

	renderCopyAction() {
		const {
			intl: { formatMessage },
			onCopy,
		} = this.props;

		const copyWithAnalytics = (
			event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
			arg1: AnalyticsEvent,
		) => {
			onCopy(event, arg1);
			this.props.returnFocus && this.props.returnFocus();
		};

		return (
			<DropdownItemWithAnalytics
				/* @ts-expect-error - Type '(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, arg1: AnalyticsEvent) => void' is not assignable to type '(e: MouseEvent<Element, MouseEvent> | KeyboardEvent<Element>, args_1: UIAnalyticsEvent) => any'. */
				onClick={copyWithAnalytics}
			>
				{formatMessage(messages.copyAction)}
			</DropdownItemWithAnalytics>
		);
	}

	renderMoveAction() {
		const {
			intl: { formatMessage },
			onMove,
		} = this.props;

		return (
			/* @ts-expect-error - Type '(arg1: UIAnalyticsEvent) => void' is not assignable to type '(e: MouseEvent<Element, MouseEvent> | KeyboardEvent<Element>, args_1: UIAnalyticsEvent) => any'. */
			<DropdownItemWithAnalytics onClick={onMove}>
				{formatMessage(messages.moveAction)}
			</DropdownItemWithAnalytics>
		);
	}

	renderDeleteAction() {
		const {
			intl: { formatMessage },
			onDelete,
			returnFocus,
		} = this.props;

		const deleteWithAnalytics = (
			event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
			arg1: AnalyticsEvent,
		) => {
			onDelete(event, arg1);
			returnFocus && returnFocus();
		};

		return (
			/* @ts-expect-error - Type '(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, arg1: AnalyticsEvent) => void' is not assignable to type '(e: MouseEvent<Element, MouseEvent> | KeyboardEvent<Element>, args_1: UIAnalyticsEvent) => any'. */
			<DropdownItemWithAnalytics onClick={deleteWithAnalytics}>
				{formatMessage(messages.deleteAction)}
			</DropdownItemWithAnalytics>
		);
	}

	render() {
		const { isOpen } = this.state;
		const {
			name,
			showSpinner,
			intl: { formatMessage },
			isSimple,
			isPrivate,
			canEdit,
		} = this.props;

		if (isSimple) {
			return null;
		}

		// legacy logic: https://stash.atlassian.com/projects/JIRACLOUD/repos/jira/browse/jira-components/jira-plugins/jira-agile/greenhopper/src/main/resources/includes/js/rapid/configuration/ManageBoards.soy
		// check boards spec for more informations: https://extranet.atlassian.com/pages/viewpage.action?pageId=3540485825
		// corresponding issue: https://jdog.jira-dev.com/browse/KITKAT-274
		let moveItem = null;
		let deleteItem = null;
		let copyItem = null;
		let settingsItem = null;

		if (isPrivate) {
			if (canEdit) {
				moveItem = this.renderMoveAction();
			}
			deleteItem = this.renderDeleteAction();
		} else {
			if (canEdit) {
				moveItem = this.renderMoveAction();
				deleteItem = this.renderDeleteAction();
			}
			copyItem = this.renderCopyAction();
			settingsItem = this.renderSettingsAction();
		}

		// @ts-expect-error - TS7031 - Binding element 'triggerRef' implicitly has an 'any' type.
		const triggerFunction = ({ triggerRef, ...props }) => {
			const newLabel = isPrivate
				? formatMessage(messages.tooltipPrivate)
				: formatMessage(messages.tooltipWithName, { name });

			const ref = mergeRefs(this.props.moreActionsRef ?? null, triggerRef);

			return (
				<IconButton
					{...props}
					appearance="subtle"
					icon={MoreIcon}
					ref={ref}
					label={name ? newLabel : formatMessage(messages.tooltip)}
					isLoading={showSpinner}
				/>
			);
		};

		return (
			<Flex justifyContent="end" xcss={isVisualRefreshEnabled() ? undefined : containerStyles}>
				<DropdownMenuWithAnalytics
					placement="bottom-end"
					isOpen={isOpen}
					onOpenChange={this.onOpenChange}
					trigger={triggerFunction}
					shouldRenderToParent
				>
					<DropdownItemGroup>
						{settingsItem}
						{copyItem}
						{moveItem}
						{deleteItem}
					</DropdownItemGroup>
				</DropdownMenuWithAnalytics>
			</Flex>
		);
	}
}

const ActionsOldWrapped = (props: Props) => {
	const { setReturnFocusTo } = useModalDialogActions();
	const moreActionsButtonRef = useRef<HTMLButtonElement>(null);

	const returnFocus = useCallback(() => {
		// To get return focus back to More Actions Element
		setReturnFocusTo(moreActionsButtonRef);
	}, [setReturnFocusTo]);

	return <ActionsOld {...props} returnFocus={returnFocus} moreActionsRef={moreActionsButtonRef} />;
};

// @ts-expect-error - TS2769 - No overload matches this call.
export default injectIntl(ActionsOldWrapped);

const containerStyles = xcss({
	paddingTop: 'space.050',
	paddingRight: 'space.025',
	paddingBottom: 'space.0',
	paddingLeft: 'space.0',
});
